import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CohortsManagement() {
  const navigate = useNavigate();
  const [cohorts, setCohorts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { getToken } = useAdminAuth();

  // Create/Edit Modal State
  const [cohortForm, setCohortForm] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    max_students: '',
    status: 'upcoming'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchCohorts();
  }, [statusFilter]);

  const fetchCohorts = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/cohorts?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      setCohorts(response.data.cohorts);
    } catch (error) {
      console.error('Error fetching cohorts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCohort = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/cohorts`,
        cohortForm,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      
      alert('Cohorte creado exitosamente');
      setShowCreateModal(false);
      setCohortForm({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        max_students: '',
        status: 'upcoming'
      });
      fetchCohorts();
    } catch (error) {
      console.error('Error creating cohort:', error);
      alert(error.response?.data?.detail || 'Error al crear el cohorte');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCohort = async (cohortId, cohortName) => {
    if (!window.confirm(`¿Estás seguro de eliminar el cohorte "${cohortName}"?`)) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/cohorts/${cohortId}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      alert('Cohorte eliminado exitosamente');
      fetchCohorts();
    } catch (error) {
      console.error('Error deleting cohort:', error);
      alert(error.response?.data?.detail || 'Error al eliminar el cohorte');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      upcoming: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      active: 'bg-green-500/10 text-green-400 border-green-500/30',
      completed: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
      archived: 'bg-red-500/10 text-red-400 border-red-500/30',
    };
    return styles[status] || styles.upcoming;
  };

  const getStatusLabel = (status) => {
    const labels = {
      upcoming: 'Próximo',
      active: 'Activo',
      completed: 'Completado',
      archived: 'Archivado',
    };
    return labels[status] || status;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Gestión de Cohortes</h1>
          <p className="text-gray-400 mt-1">Administra los cohortes y sus estudiantes</p>
        </div>
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-6 py-3 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#F4E4B7] transition-colors"
        >
          + Crear Cohorte
        </button>
      </div>

      {/* Filters */}
      <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
        <div className="flex gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
          >
            <option value="">Todos los status</option>
            <option value="upcoming">Próximos</option>
            <option value="active">Activos</option>
            <option value="completed">Completados</option>
            <option value="archived">Archivados</option>
          </select>
        </div>
      </div>

      {/* Cohorts Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div>
            <p className="mt-4 text-gray-400">Cargando cohortes...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cohorts.map((cohort) => (
            <div
              key={cohort.id}
              className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30 hover:border-[#D4AF37]/50 transition-all cursor-pointer"
              onClick={() => navigate(`/admin/cohorts/${cohort.id}`)}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">{cohort.name}</h3>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(
                      cohort.status
                    )}`}
                  >
                    {getStatusLabel(cohort.status)}
                  </span>
                </div>
              </div>

              <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                {cohort.description || 'Sin descripción'}
              </p>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Inicio:</span>
                  <span className="text-white">
                    {new Date(cohort.start_date).toLocaleDateString('es-ES')}
                  </span>
                </div>
                {cohort.end_date && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Fin:</span>
                    <span className="text-white">
                      {new Date(cohort.end_date).toLocaleDateString('es-ES')}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Estudiantes:</span>
                  <span className="text-[#D4AF37] font-bold">
                    {cohort.user_count || 0}
                    {cohort.max_students ? ` / ${cohort.max_students}` : ''}
                  </span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-[#1a4d5c]/30 flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/admin/cohorts/${cohort.id}`);
                  }}
                  className="flex-1 px-3 py-2 bg-[#1a4d5c]/30 text-[#D4AF37] rounded hover:bg-[#1a4d5c]/50 transition-colors text-sm"
                >
                  Ver Detalle
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCohort(cohort.id, cohort.name);
                  }}
                  className="px-3 py-2 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20 transition-colors text-sm"
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}

          {cohorts.length === 0 && (
            <div className="col-span-3 text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-400">No hay cohortes</h3>
              <p className="mt-1 text-sm text-gray-500">Crea tu primer cohorte para comenzar.</p>
            </div>
          )}
        </div>
      )}

      {/* Create Cohort Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] rounded-lg max-w-2xl w-full border border-[#1a4d5c]/30 p-6">
            <h3 className="text-2xl font-bold text-white mb-4">Crear Nuevo Cohorte</h3>

            <form onSubmit={handleCreateCohort} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Nombre del Cohorte *</label>
                <input
                  type="text"
                  required
                  value={cohortForm.name}
                  onChange={(e) => setCohortForm({ ...cohortForm, name: e.target.value })}
                  className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                  placeholder="Ej: 2025-Q1"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Descripción</label>
                <textarea
                  value={cohortForm.description}
                  onChange={(e) => setCohortForm({ ...cohortForm, description: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                  placeholder="Descripción breve del cohorte..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Fecha de Inicio *</label>
                  <input
                    type="date"
                    required
                    value={cohortForm.start_date}
                    onChange={(e) => setCohortForm({ ...cohortForm, start_date: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Fecha de Fin</label>
                  <input
                    type="date"
                    value={cohortForm.end_date}
                    onChange={(e) => setCohortForm({ ...cohortForm, end_date: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Máximo de Estudiantes</label>
                  <input
                    type="number"
                    min="1"
                    value={cohortForm.max_students}
                    onChange={(e) => setCohortForm({ ...cohortForm, max_students: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    placeholder="Ej: 30"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Status</label>
                  <select
                    value={cohortForm.status}
                    onChange={(e) => setCohortForm({ ...cohortForm, status: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                  >
                    <option value="upcoming">Próximo</option>
                    <option value="active">Activo</option>
                    <option value="completed">Completado</option>
                    <option value="archived">Archivado</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#F4E4B7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Creando...' : 'Crear Cohorte'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCohortForm({
                      name: '',
                      description: '',
                      start_date: '',
                      end_date: '',
                      max_students: '',
                      status: 'upcoming'
                    });
                  }}
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
